import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api-client";

import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";

export interface PlatformSetting {
	key: string;
	label: string;
	group: string;
	isSecret: boolean;
	isSet: boolean;
	source: "db" | "env" | null;
	// Always null for secrets — the backend never returns secret values.
	value: string | null;
}

export interface PlatformSettingUpdate {
	key: string;
	// null clears the DB override (falls back to env).
	value: string | null;
}

export interface TemplateAgent {
	id: string;
	name: string;
}

export interface WorkspaceVoice {
	id: string;
	name: string;
	previewUrl: string | null;
}

export const platformSettingsKeys = {
	all: ["platformSettings"] as const,
	list: () => [...platformSettingsKeys.all, "list"] as const,
	templateAgents: () => [...platformSettingsKeys.all, "template-agents"] as const,
	workspaceVoices: () => [...platformSettingsKeys.all, "workspace-voices"] as const,
};

// Returns `null` when the endpoint is not deployed yet (404) so the page can
// show an informational alert instead of an error.
export function usePlatformSettings() {
	return useQuery({
		networkMode: "always",
		queryKey: platformSettingsKeys.list(),
		queryFn: async (): Promise<PlatformSetting[] | null> => {
			try {
				const response = await apiClient.get("/platform-settings");
				return response.data as PlatformSetting[];
			} catch (error: any) {
				if (error?.response?.status === 404) {
					return null;
				}
				throw error;
			}
		},
	});
}

// Agents that exist in the platform workspace tenant, used as options for the
// AGENT_TEMPLATE_* mapping dropdowns. Returns `null` when the endpoint is not
// deployed yet (404) so the page can degrade gracefully.
export function useTemplateAgents() {
	return useQuery({
		networkMode: "always",
		queryKey: platformSettingsKeys.templateAgents(),
		queryFn: async (): Promise<TemplateAgent[] | null> => {
			try {
				const response = await apiClient.get("/platform-settings/template-agents");
				const data = response.data;
				return Array.isArray(data) ? data : ([]);
			} catch (error: any) {
				if (error?.response?.status === 404) {
					return null;
				}
				throw error;
			}
		},
	});
}

// Voices available in the AX Platform workspace's TTS plugin (ElevenLabs),
// used to curate VOICE_OPTIONS. Returns `null` when the endpoint is not
// deployed yet (404) so the page can degrade gracefully.
export function useWorkspaceVoices() {
	return useQuery({
		networkMode: "always",
		queryKey: platformSettingsKeys.workspaceVoices(),
		queryFn: async (): Promise<WorkspaceVoice[] | null> => {
			try {
				const response = await apiClient.get("/platform-settings/workspace-voices");
				const data = response.data;
				return Array.isArray(data) ? data : ([]);
			} catch (error: any) {
				if (error?.response?.status === 404) {
					return null;
				}
				throw error;
			}
		},
	});
}

export function useUpdatePlatformSettings() {
	const queryClient = useQueryClient();

	return useMutation({
		networkMode: "always",
		mutationFn: async (data: { settings: PlatformSettingUpdate[] }): Promise<void> => {
			await apiClient.put("/platform-settings", data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: platformSettingsKeys.all });
			toast.success("Platform settings saved");
		},
		onError: (error: any) => {
			// Never `logger.error(error)`: an axios error carries `config.data`, which here is
			// the PUT body - the plaintext secret that was just rejected.
			logger.error("Failed to save platform settings", error?.response?.status, error?.response?.data);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Something went wrong!");
		},
	});
}
